import { getIntakeFormsByFormIds } from "@/app/directories/companion/(main)/roster/@consumer/consumer/[consumerId]/helper";
import type { GetIntakeFormsAndQuestionsByFormIdsQueryForm as IntakeForm } from "@/lib/graphql/gql-types";
import type { IntakeFormSavedData } from "@frontend/components/intake/Intake";
import { getIntakeFormResponse } from "@frontend/lib/actions/actions";
import { LanguageCode } from "@frontend/lib/graphql/generated-gql";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export type IntakeData = {
  form: IntakeForm;
  response: IntakeFormSavedData | undefined;
  isComplete?: boolean;
};

export function translateIntakeForm(intakeFormToQuestions, locale) {
  // Deep clone the entire array of questions
  const translatedData = intakeFormToQuestions.map((question) => {
    // Create a deep clone of the question object
    const clonedQuestion = JSON.parse(JSON.stringify(question));

    // Store original values
    clonedQuestion.intakeQuestion.originalText =
      question.intakeQuestion.questionText;
    clonedQuestion.intakeQuestion.originalAttributes = JSON.parse(
      JSON.stringify(question.intakeQuestion.questionAttributes),
    );

    // If locale is 'en' or not provided, return cloned question without translation
    if (!locale || locale === "en") {
      return clonedQuestion;
    }

    const translations = question.intakeQuestion.intakeQuestionTranslations;
    const translation = translations?.find((t) => t.languageCode === locale);

    if (translation) {
      // Override questionText if translation exists
      clonedQuestion.intakeQuestion.questionText = translation.questionText;

      // Override questionAttributes if they exist in translation
      if (translation.questionAttributes) {
        const original = clonedQuestion.intakeQuestion.questionAttributes;
        const translated = translation.questionAttributes;

        // If there are choices, only override their values
        if (original?.choices && translated?.choices) {
          clonedQuestion.intakeQuestion.questionAttributes = {
            ...original,
            choices: original.choices.map((choice, index) => ({
              ...choice,
              value: translated.choices[index]?.value || choice.value,
            })),
          };
        }

        if (original.blurb && translated.blurb) {
          clonedQuestion.intakeQuestion.questionAttributes = {
            ...original,
            blurb: translated.blurb,
          };
        }
      }
    }

    return clonedQuestion;
  });

  return translatedData;
}

//@TODO: wait for Ameya
export const useIntakeForm = (
  intakeFormIds: number[],
  appointmentId: string,
  locale?: LanguageCode,
) => {
  const { data: intakeForms, isLoading: isIntakeFormsLoading } = useQuery({
    queryKey: ["getIntakeFormsAndQuestionsByFormIds", intakeFormIds, locale],
    queryFn: () => getIntakeFormsByFormIds(intakeFormIds, true),
    placeholderData: [],
    select: (forms) =>
      forms.map((form) => ({
        ...form,
        intakeFormToQuestions: translateIntakeForm(
          form.intakeFormToQuestions,
          locale ?? LanguageCode.En,
        )
          .map((question) => ({
            ...question,
            intakeQuestion: {
              ...question.intakeQuestion,
              questionAttributes: {
                ...question.intakeQuestion.questionAttributes,
                choices:
                  question.intakeQuestion.questionAttributes.choices?.map(
                    (c, index) => ({
                      ...c,
                      id: index.toString(),
                    }),
                  ),
              },
            },
          }))
          .sort((a, b) => a.questionNumber - b.questionNumber),
      })),
    enabled: intakeFormIds?.length > 0,
  });

  const { data: intakeResponse, isLoading: isIntakeResponseLoading } = useQuery(
    {
      queryFn: () => getIntakeFormResponse(appointmentId, intakeFormIds ?? []),
      queryKey: ["getIntakeFormResponse", appointmentId, intakeFormIds],
      placeholderData: [],
      enabled: intakeFormIds?.length > 0,
    },
  );

  return useMemo(() => {
    const safeIntakeForms = intakeForms ?? [];
    const safeIntakeResponse = intakeResponse ?? [];

    const intakeData = safeIntakeForms.map((form) => ({
      form: form,
      response: safeIntakeResponse.find((x) => x.intakeFormId === form.id),
    }));

    const areAllIntakeFormsCompleted = intakeData.every(
      (data) => !!data.response?.isComplete,
    );

    return {
      intakeData,
      areAllIntakeFormsCompleted,
      isLoading: isIntakeResponseLoading || isIntakeFormsLoading,
    };
  }, [
    intakeForms,
    intakeResponse,
    isIntakeFormsLoading,
    isIntakeResponseLoading,
  ]);
};
